import { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';

import { useTireInventoryList } from '../../../stores/tireInventoryStore';
import { useCompanyList } from '../../../stores/companyStore';
import DataTable from '../../../components/DataTable';
import DetailTireInventory from './DetailTireInventory';
import { useLanguage } from '../../../utils/i18n';
export default function TireInventoryPage() {
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const { t } = useLanguage();
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);

  const handleOpenDetail = (batchRow) => {
    if (!batchRow?.batch_number || batchRow.batch_number === '-') return;
    setSelectedBatch(batchRow);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedBatch(null);
  };

  const [searchField, setSearchField] = useState('all');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.all', 'All') },
      { value: 'batch_number', label: t('tireReceiving.batchNo', 'Batch No') },
      { value: 'qty', label: t('tireReceiving.qty', 'Qty') },
      { value: 'receive_date', label: t('tireReceiving.receiveDate', 'Receive Date') },
      { value: 'tire_brand_name', label: t('specification.brand', 'Brand') },
      { value: 'tire_size_code_internal', label: t('specification.size', 'Size') },
      { value: 'tire_spec_name', label: t('specification.spec_name', 'Spec Name') },
      { value: 'warehouse_name', label: t('warehouse.name', 'Warehouse Name') },
      { value: 'supplier', label: t('tireReceiving.supplier', 'Supplier') },
      { value: 'unit_price', label: t('tireReceiving.unitPrice', 'Unit Price') }
    ],
    [t]
  );

  const { companyOptions } = useCompanyList();
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(() => {
    return (companyOptions || []).map((c) => ({
      value: String(c.value ?? c.id ?? c),
      label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
    }));
  }, [companyOptions]);

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const handleCompanyChange = (val) => {
    setSelectedCompanies(val);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSearch = ({ field, value }) => {
    setSearchField(field || 'all');
    setSearchRaw(value);
    setSearchValue(value);
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  // Fetch data
  const { data, totalRecord, isLoading } = useTireInventoryList(
    paginationModel.page,
    paginationModel.pageSize,
    selectedCompanies,
    searchField,
    searchValue
  );

  const fetchAllForExport = useCallback(async () => {
    // For large tables, you might want to call a fetch without pagination
    // Here we'll return the current loaded data for export
    return data || [];
  }, [data]);

  const columns = useMemo(
    () => [
      {
        field: 'action',
        headerName: 'Action',
        width: 120,
        sortable: false,
        renderCell: (params) => {
          const batchNo = params.row?.batch_number;
          return (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => handleOpenDetail(params.row)}
              disabled={!batchNo || batchNo === '-'}
            >
              {t('details', 'Details')}
            </Button>
          );
        }
      },
      {
        field: 'company_name',
        headerName: t('general.company', 'Company'),
        width: 150,
        valueGetter: (value, row) => row.company_name || '-'
      },
      {
        field: 'batch_number',
        headerName: t('tireReceiving.batchNo', 'Batch Number'),
        width: 150,
        valueGetter: (value, row) => row.batch_number || '-'
      },
      {
        field: 'qty',
        headerName: t('tireReceiving.qty', 'Qty'),
        width: 80,
        valueGetter: (value, row) => row.qty ?? '-'
      },
      {
        field: 'receive_date',
        headerName: t('tireReceiving.receivedDate', 'Received Date'),
        width: 150,
        valueGetter: (value, row) => row.receive_date || '-',
        valueFormatter: (value) => {
          if (!value || value === '-') return '-';
          return dayjs(value).format('DD-MM-YYYY');
        }
      },
      {
        field: 'tire_brand_name',
        headerName: t('specification.brand', 'Brand'),
        width: 150,
        valueGetter: (value, row) => row.tire_brand_name || '-'
      },
      {
        field: 'tire_size_code',
        headerName: t('specification.size', 'Size'),
        width: 120,
        valueGetter: (value, row) => row.tire_size_code_internal || row.tire_size_code || '-'
      },
      {
        field: 'tire_spec_name',
        headerName: t('specification.spec_name', 'Spec Name'),
        width: 150,
        valueGetter: (value, row) => row.tire_spec_name || row.pattern || '-'
      },
      {
        field: 'warehouse_name',
        headerName: t('warehouse.name', 'Warehouse'),
        width: 150,
        valueGetter: (value, row) => row.warehouse_name || '-'
      },
      {
        field: 'supplier',
        headerName: t('tireReceiving.supplier', 'Supplier'),
        width: 150,
        valueGetter: (value, row) => row.supplier || '-'
      },
      {
        field: 'original_tread_depth',
        headerName: t('specification.originalTreadDepth', 'Original Tread Depth'),
        width: 120,
        hide: true,
        valueGetter: (value, row) => row.original_tread_depth ?? '-'
      },
      {
        field: 'unit_price',
        headerName: t('tireReceiving.unitPrice', 'Unit Price'),
        width: 150,
        valueGetter: (value, row) => row.unit_price,
        valueFormatter: (value) => {
          if (value == null || value === '') return '-';
          return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(Number(value));
        }
      }
    ],
    [t]
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        {t('menu.tire-inventory', 'Tire Inventory')}
      </Typography>
      <DataTable
        rows={data || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 25, 50, 100]}
        hideAddButton={true}
        hideExportButton={false}
        paginationMode="server"
        rowCount={totalRecord || 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => handleSearch({ field, value })}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          const safeField = typeof f === 'object' ? f?.value : f;
          setSearchField(safeField || 'all');
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={fetchAllForExport}
      />

      <DetailTireInventory open={detailOpen} onClose={handleCloseDetail} batchRow={selectedBatch} selectedCompanies={selectedCompanies} />
    </Box>
  );
}
